import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, CreditCard, Loader2, ArrowLeft, Tag, X, User, Building, FileText, MapPin } from 'lucide-react';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import { usePaypalClientId } from '../hooks/usePaypalClientId';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscribePage() {
  const { language, t } = useLanguage();
  const { user, token, isAuthenticated, refreshUser, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [processing, setProcessing] = useState(false);
  const [paypalTransaction, setPaypalTransaction] = useState(null);
  const paypalClientId = usePaypalClientId();
  const [step, setStep] = useState(1); // 1: select plan, 2: registration form, 3: payment
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discountedPrice, setDiscountedPrice] = useState(null);

  // Registration form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    // Billing info
    company_name: '',
    vat_number: '',
    fiscal_code: '',
    sdi_code: '',
    pec_email: '',
    address: '',
    city: '',
    province: '',
    postal_code: '',
    country: 'Italia',
    // Consents
    privacy_accepted: false,
    marketing_accepted: false
  });
  const [formErrors, setFormErrors] = useState({});

  const planIdFromUrl = searchParams.get('plan');

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    if (plans.length > 0 && planIdFromUrl) {
      const plan = plans.find(p => p.id === planIdFromUrl);
      if (plan) {
        setSelectedPlan(plan);
        if (plan.id.includes('annual')) {
          setBillingCycle('annual');
        }
      }
    }
  }, [plans, planIdFromUrl]);

  useEffect(() => {
    setAppliedCoupon(null);
    setDiscountedPrice(null);
    setCouponCode('');
  }, [selectedPlan?.id]);

  // If user is authenticated, skip to step 3
  useEffect(() => {
    if (isAuthenticated && selectedPlan && step === 2) {
      setStep(3);
    }
  }, [isAuthenticated, selectedPlan, step]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Errore nel caricamento dei piani');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredPlans = () => {
    return plans.filter(plan => {
      // Always exclude report packs from the main subscription grid:
      // they are top-ups and can be bought from the user dashboard only.
      if (plan.category === 'reports') return false;
      if (plan.id === 'trial') return true;
      return plan.id.includes(billingCycle);
    });
  };

  const handleSelectPlan = (plan) => {
    if (plan.category === 'reports') {
      // Report packs are top-ups; require an authenticated, active subscription
      if (!isAuthenticated) {
        toast.error('Devi accedere e attivare prima un abbonamento per acquistare un pacchetto Report');
        return;
      }
      if (!user?.plan || user.plan === 'trial' || user.plan.startsWith('reports_') || !user.is_active) {
        toast.error('I pacchetti Report sono ricariche per abbonamenti attivi. Attiva prima un piano Starter o Professional.');
        return;
      }
    }
    setSelectedPlan(plan);
    if (isAuthenticated) {
      setStep(3);
    } else {
      setStep(2);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.email) errors.email = 'Email richiesta';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email non valida';
    
    if (!formData.password) errors.password = 'Password richiesta';
    else if (formData.password.length < 6) errors.password = 'Minimo 6 caratteri';
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Le password non coincidono';
    }
    
    if (!formData.name) errors.name = 'Nome e cognome richiesti';
    if (!formData.phone) errors.phone = 'Telefono richiesto';
    if (!formData.address) errors.address = 'Indirizzo richiesto';
    if (!formData.city) errors.city = 'Città richiesta';
    if (!formData.province) errors.province = 'Provincia richiesta';
    if (!formData.postal_code) errors.postal_code = 'CAP richiesto';
    
    // Either VAT number or fiscal code is required
    if (!formData.vat_number && !formData.fiscal_code) {
      errors.vat_number = 'P.IVA o Codice Fiscale richiesto';
    }

    // Either SDI code or PEC is mandatory for electronic invoicing
    const sdi = (formData.sdi_code || '').trim();
    const pec = (formData.pec_email || '').trim();
    if (!sdi && !pec) {
      errors.sdi_code = 'Inserisci Codice SDI o PEC (almeno uno obbligatorio)';
      errors.pec_email = 'Inserisci Codice SDI o PEC (almeno uno obbligatorio)';
    } else if (pec && !/\S+@\S+\.\S+/.test(pec)) {
      errors.pec_email = 'PEC non valida';
    }

    if (!formData.privacy_accepted) {
      errors.privacy_accepted = 'Devi accettare la privacy policy';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // === Phase-2 pending registration: form data is saved server-side; account
  //     creation happens AFTER payment success via Stripe webhook / PayPal capture.
  const [pendingId, setPendingId] = useState(null);
  const [tempToken, setTempToken] = useState(null);

  const handleRegisterAndContinue = async () => {
    if (!validateForm()) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API}/auth/register-pending`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        billing_info: {
          company_name: formData.company_name || null,
          vat_number: formData.vat_number || null,
          fiscal_code: formData.fiscal_code || null,
          sdi_code: formData.sdi_code || null,
          pec_email: formData.pec_email || null,
          address: formData.address,
          city: formData.city,
          province: formData.province,
          postal_code: formData.postal_code,
          country: formData.country
        },
        privacy_accepted: formData.privacy_accepted,
        marketing_accepted: formData.marketing_accepted
      });

      setPendingId(response.data.pending_id);
      setTempToken(response.data.temp_token);
      // Persist so user can come back from Stripe redirect
      localStorage.setItem('bond_pending_id', response.data.pending_id);
      localStorage.setItem('bond_pending_token', response.data.temp_token);

      toast.success('Dati salvati. Procedi al pagamento per completare la registrazione.');
      setStep(3);
    } catch (error) {
      console.error('Pending registration error:', error);
      toast.error(error.response?.data?.detail || 'Errore nella registrazione');
    } finally {
      setProcessing(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim() || !selectedPlan) return;
    
    setCouponLoading(true);
    try {
      const response = await axios.post(`${API}/coupons/validate`, {
        code: couponCode.trim(),
        plan_id: selectedPlan.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.valid) {
        setAppliedCoupon({
          code: couponCode.trim().toUpperCase(),
          discount_type: response.data.discount_type,
          discount_value: response.data.discount_value,
          message: response.data.message
        });
        setDiscountedPrice(response.data.final_price);
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
        setAppliedCoupon(null);
        setDiscountedPrice(null);
      }
    } catch (error) {
      console.error('Coupon error:', error);
      toast.error(error.response?.data?.detail || 'Errore nella verifica del coupon');
      setAppliedCoupon(null);
      setDiscountedPrice(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscountedPrice(null);
    setCouponCode('');
  };

  const handleStripeCheckout = async () => {
    if (!selectedPlan) return;

    // Branch A: existing logged-in user buying a report pack (or any plan)
    if (isAuthenticated) {
      setProcessing(true);
      try {
        const response = await axios.post(`${API}/payments/checkout`, {
          plan_id: selectedPlan.id,
          origin_url: window.location.origin,
          payment_method: 'stripe',
          coupon_code: appliedCoupon?.code || null
        }, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        } else if (response.data.success) {
          await refreshUser();
          navigate('/payment/success?free=true');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Errore nel checkout');
      } finally {
        setProcessing(false);
      }
      return;
    }

    // Branch B: new user via pending registration → verify €1 charge for free trial
    if (!tempToken) {
      toast.error('Sessione persa. Ricomincia la registrazione.');
      setStep(2);
      return;
    }
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/payments/checkout-pending`, {
        plan_id: selectedPlan.id,
        origin_url: window.location.origin,
        payment_method: 'stripe'
      }, { headers: { 'X-Temp-Token': tempToken } });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout-pending error:', error);
      toast.error(error.response?.data?.detail || 'Errore nel checkout');
    } finally {
      setProcessing(false);
    }
  };

  const handlePayPalInit = async () => {
    if (!selectedPlan) return;

    const endpoint = isAuthenticated ? `${API}/payments/checkout` : `${API}/payments/checkout-pending`;
    const headers = isAuthenticated
      ? { Authorization: `Bearer ${token}` }
      : { 'X-Temp-Token': tempToken };
    const body = isAuthenticated
      ? { plan_id: selectedPlan.id, origin_url: window.location.origin, payment_method: 'paypal', coupon_code: appliedCoupon?.code || null }
      : { plan_id: selectedPlan.id, origin_url: window.location.origin, payment_method: 'paypal' };

    try {
      const response = await axios.post(endpoint, body, { headers });
      setPaypalTransaction(response.data);
      return response.data.paypal_order_id;
    } catch (error) {
      console.error('PayPal init error:', error);
      toast.error(error.response?.data?.detail || 'Errore PayPal');
      throw error;
    }
  };

  const handlePayPalCapture = async (orderId, payerId) => {
    try {
      if (isAuthenticated) {
        const response = await axios.post(`${API}/payments/paypal/capture`, {
          transaction_id: paypalTransaction?.transaction_id,
          order_id: orderId,
        }, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success) {
          await refreshUser();
          navigate('/payment/success?session_id=' + paypalTransaction?.transaction_id);
        }
      } else {
        await axios.post(`${API}/payments/paypal/capture-pending`, {
          transaction_id: paypalTransaction?.transaction_id,
          order_id: orderId,
          payer_id: payerId,
        }, { headers: { 'X-Temp-Token': tempToken } });
        // Redirect to success page which will poll pending-status
        navigate('/payment/success?pending_id=' + pendingId);
      }
    } catch (error) {
      console.error('PayPal capture error:', error);
      toast.error(error.response?.data?.detail || 'Errore PayPal');
    }
  };

  const filteredPlans = getFilteredPlans();
  const isFree = selectedPlan?.price === 0;
  const finalPrice = discountedPrice !== null ? discountedPrice : selectedPlan?.price;
  const hasDiscount = appliedCoupon && discountedPrice !== null && discountedPrice < selectedPlan?.price;

  return (
    <div className="min-h-screen bg-smartBlack-DEFAULT flex flex-col">
      <Navbar onRegisterClick={() => {}} />
      
      <main className="flex-1 pt-28 pb-16 px-6 md:px-12 lg:px-24" data-testid="subscribe-page">
        <div className="max-w-6xl mx-auto">
          {/* Back button */}
          <button 
            onClick={() => step > 1 ? setStep(step - 1) : navigate('/')}
            className="flex items-center gap-2 text-smartGrey-muted hover:text-white mb-8 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={18} />
            {step > 1 ? 'Indietro' : 'Torna alla home'}
          </button>

          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-4 mb-12">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  s <= step ? 'bg-smartRed text-white' : 'bg-smartBlack-surface text-smartGrey-muted'
                }`}>
                  {s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-0.5 mx-2 ${s < step ? 'bg-smartRed' : 'bg-smartBlack-surface'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Select Plan */}
          {step === 1 && (
            <>
              <div className="text-center mb-12">
                <p className="section-overline mb-4">ABBONAMENTO</p>
                <h1 className="font-heading font-bold text-3xl lg:text-4xl text-smartGrey-text mb-4">
                  {language === 'it' ? 'Scegli il tuo piano' : 'Choose your plan'}
                </h1>
              </div>

              {/* Billing toggle */}
              <div className="flex justify-center mb-12">
                <div className="bg-smartBlack-card border border-smartGrey-border rounded-md p-1 inline-flex">
                  <button
                    onClick={() => setBillingCycle('monthly')}
                    className={`px-6 py-2 text-sm font-medium transition-all ${
                      billingCycle === 'monthly' 
                        ? 'bg-smartRed text-white' 
                        : 'text-smartGrey-muted hover:text-white'
                    }`}
                    data-testid="billing-monthly"
                  >
                    Mensile
                  </button>
                  <button
                    onClick={() => setBillingCycle('annual')}
                    className={`px-6 py-2 text-sm font-medium transition-all ${
                      billingCycle === 'annual' 
                        ? 'bg-smartRed text-white' 
                        : 'text-smartGrey-muted hover:text-white'
                    }`}
                    data-testid="billing-annual"
                  >
                    Annuale
                    <span className="ml-2 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                      -17%
                    </span>
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="animate-spin text-smartRed" size={32} />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto items-stretch">
                  {filteredPlans.map((plan) => {
                    const name = language === 'it' ? plan.name_it : plan.name_en;
                    const description = language === 'it' ? plan.description_it : plan.description_en;
                    const features = language === 'it' ? plan.features_it : plan.features_en;

                    return (
                      <div 
                        key={plan.id}
                        onClick={() => handleSelectPlan(plan)}
                        className={`card-smart rounded-md p-6 cursor-pointer transition-all relative hover:border-smartRed flex flex-col ${
                          plan.popular ? 'pricing-popular' : ''
                        }`}
                        data-testid={`plan-card-${plan.id}`}
                      >
                        {plan.popular && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-smartRed text-white text-xs font-bold uppercase tracking-widest px-3 py-1">
                            Consigliato
                          </div>
                        )}

                        <h3 className="font-heading font-semibold text-lg text-smartGrey-text mb-1">
                          {name.replace(' Mensile', '').replace(' Annuale', '').replace(' Monthly', '').replace(' Annual', '')}
                        </h3>
                        <p className="text-xs text-smartGrey-muted mb-4">{description}</p>
                        
                        <div className="mb-4">
                          <span className="font-heading font-bold text-3xl text-smartGrey-text">
                            {plan.price === 0 ? 'Gratis' : `€${plan.price}`}
                          </span>
                          {plan.price > 0 && (
                            <span className="text-smartGrey-muted text-sm">
                              /{billingCycle === 'annual' ? 'anno' : 'mese'}
                            </span>
                          )}
                        </div>

                        <ul className="space-y-2 mb-6 flex-grow">
                          {features?.slice(0, 4).map((feature, index) => (
                            <li key={index} className="flex items-start gap-2 text-xs">
                              <Check size={14} className="text-smartRed mt-0.5 flex-shrink-0" />
                              <span className="text-smartGrey-muted">{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <Button 
                          className={`bond-btn ${plan.popular ? 'bond-btn-primary' : 'bond-btn-secondary'} w-full mt-auto`}
                        >
                          SELEZIONA
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Step 2: Registration Form */}
          {step === 2 && selectedPlan && (
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h1 className="font-heading font-bold text-2xl lg:text-3xl text-smartGrey-text mb-2">
                  Inserisci i tuoi dati
                </h1>
                <p className="text-smartGrey-muted">
                  Piano selezionato: <span className="text-smartRed font-semibold">
                    {language === 'it' ? selectedPlan.name_it : selectedPlan.name_en}
                  </span>
                </p>
              </div>

              <div className="card-smart rounded-md p-8">
                <form onSubmit={(e) => { e.preventDefault(); handleRegisterAndContinue(); }}>
                  {/* Account Section */}
                  <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4">
                      <User size={20} className="text-smartRed" />
                      <h2 className="font-heading font-semibold text-lg text-smartGrey-text">Dati Account</h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm text-smartGrey-text mb-1">Email *</label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleFormChange}
                          className={`input-smart rounded-none h-11 ${formErrors.email ? 'border-red-500' : ''}`}
                          data-testid="reg-email"
                        />
                        {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                      </div>
                      <div>
                        <label className="block text-sm text-smartGrey-text mb-1">Password *</label>
                        <Input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleFormChange}
                          className={`input-smart rounded-none h-11 ${formErrors.password ? 'border-red-500' : ''}`}
                          data-testid="reg-password"
                        />
                        {formErrors.password && <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>}
                      </div>
                      <div>
                        <label className="block text-sm text-smartGrey-text mb-1">Conferma Password *</label>
                        <Input
                          type="password"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleFormChange}
                          className={`input-smart rounded-none h-11 ${formErrors.confirmPassword ? 'border-red-500' : ''}`}
                          data-testid="reg-confirm-password"
                        />
                        {formErrors.confirmPassword && <p className="text-red-500 text-xs mt-1">{formErrors.confirmPassword}</p>}
                      </div>
                      <div>
                        <label className="block text-sm text-smartGrey-text mb-1">Nome e Cognome *</label>
                        <Input
                          name="name"
                          value={formData.name}
                          onChange={handleFormChange}
                          className={`input-smart rounded-none h-11 ${formErrors.name ? 'border-red-500' : ''}`}
                          data-testid="reg-name"
                        />
                        {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-sm text-smartGrey-text mb-1">Telefono *</label>
                        <Input
                          name="phone"
                          value={formData.phone}
                          onChange={handleFormChange}
                          className={`input-smart rounded-none h-11 ${formErrors.phone ? 'border-red-500' : ''}`}
                          data-testid="reg-phone"
                        />
                        {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Billing Section */}
                  <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4">
                      <Building size={20} className="text-smartRed" />
                      <h2 className="font-heading font-semibold text-lg text-smartGrey-text">Dati Fatturazione</h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm text-smartGrey-text mb-1">Ragione Sociale / Azienda</label>
                        <Input
                          name="company_name"
                          value={formData.company_name}
                          onChange={handleFormChange}
                          className="input-smart rounded-none h-11"
                          data-testid="reg-company"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-smartGrey-text mb-1">Partita IVA {!formData.fiscal_code && '*'}</label>
                        <Input
                          name="vat_number"
                          value={formData.vat_number}
                          onChange={handleFormChange}
                          placeholder="IT..."
                          className={`input-smart rounded-none h-11 ${formErrors.vat_number ? 'border-red-500' : ''}`}
                          data-testid="reg-vat"
                        />
                        {formErrors.vat_number && <p className="text-red-500 text-xs mt-1">{formErrors.vat_number}</p>}
                      </div>
                      <div>
                        <label className="block text-sm text-smartGrey-text mb-1">Codice Fiscale {!formData.vat_number && '*'}</label>
                        <Input
                          name="fiscal_code"
                          value={formData.fiscal_code}
                          onChange={handleFormChange}
                          className="input-smart rounded-none h-11"
                          data-testid="reg-fiscal"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-smartGrey-text mb-1">
                          Codice SDI {!formData.pec_email && '*'}
                        </label>
                        <Input
                          name="sdi_code"
                          value={formData.sdi_code}
                          onChange={handleFormChange}
                          placeholder="7 caratteri"
                          maxLength={7}
                          className={`input-smart rounded-none h-11 ${formErrors.sdi_code ? 'border-red-500' : ''}`}
                          data-testid="reg-sdi"
                        />
                        {formErrors.sdi_code && <p className="text-red-500 text-xs mt-1">{formErrors.sdi_code}</p>}
                      </div>
                      <div>
                        <label className="block text-sm text-smartGrey-text mb-1">
                          PEC {!formData.sdi_code && '*'}
                        </label>
                        <Input
                          type="email"
                          name="pec_email"
                          value={formData.pec_email}
                          onChange={handleFormChange}
                          className={`input-smart rounded-none h-11 ${formErrors.pec_email ? 'border-red-500' : ''}`}
                          data-testid="reg-pec"
                        />
                        {formErrors.pec_email && <p className="text-red-500 text-xs mt-1">{formErrors.pec_email}</p>}
                      </div>
                    </div>
                    <p className="text-xs text-[#A3A3A3] mt-2">
                      * Almeno uno fra <strong>Codice SDI</strong> e <strong>PEC</strong> è obbligatorio per la fatturazione elettronica.
                    </p>
                  </div>

                  {/* Address Section */}
                  <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4">
                      <MapPin size={20} className="text-smartRed" />
                      <h2 className="font-heading font-semibold text-lg text-smartGrey-text">Indirizzo</h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm text-smartGrey-text mb-1">Indirizzo *</label>
                        <Input
                          name="address"
                          value={formData.address}
                          onChange={handleFormChange}
                          placeholder="Via/Piazza, numero civico"
                          className={`input-smart rounded-none h-11 ${formErrors.address ? 'border-red-500' : ''}`}
                          data-testid="reg-address"
                        />
                        {formErrors.address && <p className="text-red-500 text-xs mt-1">{formErrors.address}</p>}
                      </div>
                      <div>
                        <label className="block text-sm text-smartGrey-text mb-1">Città *</label>
                        <Input
                          name="city"
                          value={formData.city}
                          onChange={handleFormChange}
                          className={`input-smart rounded-none h-11 ${formErrors.city ? 'border-red-500' : ''}`}
                          data-testid="reg-city"
                        />
                        {formErrors.city && <p className="text-red-500 text-xs mt-1">{formErrors.city}</p>}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-smartGrey-text mb-1">Provincia *</label>
                          <Input
                            name="province"
                            value={formData.province}
                            onChange={handleFormChange}
                            placeholder="XX"
                            maxLength={2}
                            className={`input-smart rounded-none h-11 uppercase ${formErrors.province ? 'border-red-500' : ''}`}
                            data-testid="reg-province"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-smartGrey-text mb-1">CAP *</label>
                          <Input
                            name="postal_code"
                            value={formData.postal_code}
                            onChange={handleFormChange}
                            maxLength={5}
                            className={`input-smart rounded-none h-11 ${formErrors.postal_code ? 'border-red-500' : ''}`}
                            data-testid="reg-cap"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Consents */}
                  <div className="mb-8 space-y-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="privacy"
                        checked={formData.privacy_accepted}
                        onCheckedChange={(checked) => handleFormChange({ target: { name: 'privacy_accepted', type: 'checkbox', checked }})}
                        data-testid="reg-privacy"
                      />
                      <label htmlFor="privacy" className={`text-sm ${formErrors.privacy_accepted ? 'text-red-500' : 'text-smartGrey-muted'}`}>
                        Accetto la <a href="/privacy" target="_blank" className="text-smartRed hover:underline">Privacy Policy</a> e i <a href="/terms" target="_blank" className="text-smartRed hover:underline">Termini di Servizio</a> *
                      </label>
                    </div>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="marketing"
                        checked={formData.marketing_accepted}
                        onCheckedChange={(checked) => handleFormChange({ target: { name: 'marketing_accepted', type: 'checkbox', checked }})}
                        data-testid="reg-marketing"
                      />
                      <label htmlFor="marketing" className="text-sm text-smartGrey-muted">
                        Desidero ricevere comunicazioni commerciali e promozionali
                      </label>
                    </div>
                  </div>

                  <Button 
                    type="submit"
                    disabled={processing}
                    className="bond-btn bond-btn-primary w-full"
                    data-testid="continue-to-payment"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Registrazione in corso...
                      </>
                    ) : (
                      'CONTINUA AL PAGAMENTO'
                    )}
                  </Button>
                </form>
              </div>
            </div>
          )}

          {/* Step 3: Payment */}
          {step === 3 && selectedPlan && isAuthenticated && (
            <div className="max-w-md mx-auto">
              <div className="text-center mb-8">
                <h1 className="font-heading font-bold text-2xl lg:text-3xl text-smartGrey-text mb-2">
                  Completa il pagamento
                </h1>
              </div>

              <div className="card-smart rounded-md p-6">
                {/* Plan summary */}
                <div className="bg-smartBlack-surface p-4 rounded-md border border-smartGrey-border mb-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-smartGrey-text font-medium">
                        {language === 'it' ? selectedPlan.name_it : selectedPlan.name_en}
                      </p>
                      <p className="text-xs text-smartGrey-muted">
                        {isFree ? 'Prova gratuita 3 giorni' : (billingCycle === 'annual' ? 'Fatturazione annuale' : 'Fatturazione mensile')}
                      </p>
                    </div>
                    <div className="text-right">
                      {hasDiscount && (
                        <p className="text-sm text-smartGrey-muted line-through">€{selectedPlan.price}</p>
                      )}
                      <p className={`font-heading font-bold text-xl ${hasDiscount ? 'text-green-400' : 'text-smartGrey-text'}`}>
                        {isFree ? 'Gratis' : `€${finalPrice}`}
                      </p>
                    </div>
                  </div>
                  
                  {hasDiscount && (
                    <div className="mt-2 pt-2 border-t border-smartGrey-border flex justify-between items-center text-sm">
                      <span className="text-green-400">Sconto applicato</span>
                      <span className="text-green-400">-€{(selectedPlan.price - discountedPrice).toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {/* Coupon input */}
                {!isFree && (
                  <div className="mb-6">
                    <label className="block text-sm text-smartGrey-text mb-2">
                      <Tag size={14} className="inline mr-2" />
                      Codice Sconto
                    </label>
                    
                    {appliedCoupon ? (
                      <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-md p-3">
                        <div>
                          <p className="text-green-400 font-mono text-sm font-bold">{appliedCoupon.code}</p>
                          <p className="text-xs text-green-400/70">
                            {appliedCoupon.discount_type === 'percentage' 
                              ? `-${appliedCoupon.discount_value}%` 
                              : `-€${appliedCoupon.discount_value}`}
                          </p>
                        </div>
                        <button onClick={handleRemoveCoupon} className="text-green-400 hover:text-red-400">
                          <X size={18} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          placeholder="Es. WELCOME20"
                          className="input-smart rounded-none h-10 font-mono uppercase"
                          data-testid="coupon-input"
                        />
                        <Button
                          onClick={handleApplyCoupon}
                          disabled={couponLoading || !couponCode.trim()}
                          variant="outline"
                          className="bond-btn bond-btn-secondary bond-btn-small"
                        >
                          {couponLoading ? <Loader2 className="animate-spin" size={16} /> : 'Applica'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Payment buttons */}
                {isFree || finalPrice === 0 ? (
                  <Button 
                    onClick={handleStripeCheckout}
                    disabled={processing}
                    className="bond-btn bond-btn-primary w-full"
                    data-testid="activate-btn"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Attivazione...
                      </>
                    ) : (
                      'ATTIVA ORA'
                    )}
                  </Button>
                ) : (
                  <>
                    <div className="mb-4">
                      <p className="text-sm text-smartGrey-text mb-3">Metodo di pagamento</p>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setPaymentMethod('stripe')}
                          className={`p-3 border rounded-md transition-all flex flex-col items-center ${
                            paymentMethod === 'stripe' 
                              ? 'border-smartRed bg-smartRed/10' 
                              : 'border-smartGrey-border hover:border-smartGrey-muted'
                          }`}
                        >
                          <CreditCard size={20} className={paymentMethod === 'stripe' ? 'text-smartRed' : 'text-smartGrey-muted'} />
                          <span className={`text-xs mt-1 ${paymentMethod === 'stripe' ? 'text-smartGrey-text' : 'text-smartGrey-muted'}`}>Carta</span>
                        </button>
                        <button
                          onClick={() => setPaymentMethod('paypal')}
                          className={`p-3 border rounded-md transition-all flex flex-col items-center ${
                            paymentMethod === 'paypal' 
                              ? 'border-smartRed bg-smartRed/10' 
                              : 'border-smartGrey-border hover:border-smartGrey-muted'
                          }`}
                        >
                          <svg viewBox="0 0 24 24" width="20" height="20" className={paymentMethod === 'paypal' ? 'text-smartRed' : 'text-smartGrey-muted'} fill="currentColor">
                            <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z"/>
                          </svg>
                          <span className={`text-xs mt-1 ${paymentMethod === 'paypal' ? 'text-smartGrey-text' : 'text-smartGrey-muted'}`}>PayPal</span>
                        </button>
                      </div>
                    </div>

                    {paymentMethod === 'stripe' ? (
                      <Button 
                        onClick={handleStripeCheckout}
                        disabled={processing}
                        className="bond-btn bond-btn-primary w-full"
                      >
                        {processing ? (
                          <>
                            <Loader2 className="animate-spin" size={18} />
                            Elaborazione...
                          </>
                        ) : (
                          <>
                            <CreditCard size={18} />
                            PAGA €{finalPrice}
                          </>
                        )}
                      </Button>
                    ) : !paypalClientId ? (
                      <p className="text-sm text-smartGrey-muted text-center" data-testid="paypal-unavailable">Pagamento PayPal non disponibile al momento</p>
                    ) : (
                      <PayPalScriptProvider options={{ clientId: paypalClientId, currency: 'EUR' }}>
                        <PayPalButtons
                          style={{ layout: 'vertical', color: 'black', shape: 'rect', height: 45 }}
                          createOrder={handlePayPalInit}
                          onApprove={(data) => handlePayPalCapture(data.orderID)}
                          onError={(err) => {
                            console.error('PayPal error:', err);
                            toast.error('Errore PayPal');
                          }}
                        />
                      </PayPalScriptProvider>
                    )}
                  </>
                )}

                <p className="text-xs text-smartGrey-muted text-center mt-4">
                  🔒 Pagamento sicuro
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
